import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SidePanel, SidePanelContent, SidePanelFooter } from '@securspace/securspace-ui-kit';
import { ExistingCredential, credentialToFormData } from '../types';
import { updateApiCredential } from '../api-integration-requests';
import { useTokenForm } from '../hooks/useTokenForm';
import TokenFormFields from './TokenFormFields';
import { getErrorMessageForStandardResponse } from '../../../util/NetworkErrorUtil';

interface EditTokenPanelProps {
  open: boolean;
  credential: ExistingCredential | null;
  onClose: () => void;
  onTokenUpdated?: () => void;
}

/**
 * Side panel for editing existing API token metadata.
 * Simpler than CreateTokenPanel - no credentials display needed.
 */
const EditTokenPanel: React.FC<EditTokenPanelProps> = ({
  open,
  credential,
  onClose,
  onTokenUpdated,
}) => {
  // Form state
  const initialData = credential ? credentialToFormData(credential) : undefined;
  const [formDirty, setFormDirty] = useState(false);

  const {
    formData,
    emailErrorMsg,
    firstNameErrorMsg,
    lastNameErrorMsg,
    phoneNumberErrorMsg,
    isFormValid,
    handleFieldChange,
    handlePhoneNumberChange,
    resetForm,
    getSubmitPayload,
  } = useTokenForm({
    initialData,
    onDirtyChange: setFormDirty
  });

  // Submission state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [discardChangesDialog, setDiscardChangesDialog] = useState(false);

  // Reset form when credential changes - validate on reset to catch missing required fields
  useEffect(() => {
    if (credential) {
      resetForm(credentialToFormData(credential), true);
    }
    setError(null);
    setFormDirty(false);
  }, [credential, resetForm]);

  const handleClose = (forceClose: boolean = false) => {
    if (forceClose) {
      setDiscardChangesDialog(false);
      setError(null);
      onClose();
    }
  };

  const handleCloseAttempt = useCallback((): boolean => {
    if (formDirty) {
      setDiscardChangesDialog(true);
      return false;
    }
    return true;
  }, [formDirty]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!credential || !isFormValid) return;

    setLoading(true);
    setError(null);

    try {
      await updateApiCredential(credential.id, getSubmitPayload());
      onTokenUpdated?.();
      handleClose(true);
    } catch (err: unknown) {
      const errorMessage = getErrorMessageForStandardResponse(err, 'Failed to update API token. Please try again.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SidePanel
        open={open}
        onClose={onClose}
        onCloseAttempt={handleCloseAttempt}
        title="Edit API Token"
      >
        {/* Content */}
        <SidePanelContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <TokenFormFields
              formData={formData}
              loading={loading}
              emailErrorMsg={emailErrorMsg}
              firstNameErrorMsg={firstNameErrorMsg}
              lastNameErrorMsg={lastNameErrorMsg}
              phoneNumberErrorMsg={phoneNumberErrorMsg}
              onFieldChange={handleFieldChange}
              onPhoneNumberChange={handlePhoneNumberChange}
            />
          </form>
        </SidePanelContent>

        {/* Footer */}
        <SidePanelFooter>
          <Button
            onClick={handleSubmit}
            variant={'contained'}
            color={'primary'}
            disabled={loading || !isFormValid}
            fullWidth
            sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </SidePanelFooter>
      </SidePanel>

      {/* Discard Changes Dialog */}
      <Dialog
        open={discardChangesDialog}
        onClose={() => setDiscardChangesDialog(false)}
        aria-labelledby="discard-changes-title"
      >
        <DialogTitle id="discard-changes-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Discard Unsaved Changes?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to close and lose your progress?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDiscardChangesDialog(false)}
            variant="contained"
            color="primary"
          >
            Keep Editing
          </Button>
          <Button
            onClick={() => handleClose(true)}
            color="error"
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditTokenPanel;
